import { useEffect, useState } from "react";
import {
  getRecentSessions,
  getActiveSession,
  type SessionRecord,
} from "@music-app/persistence";

/**
 * Load recent sessions once on mount. Stats and Home both read this; the
 * window is intentionally generous (defaults to 60) so streak/sparkline
 * computations don't miss past days. The hook re-fetches when `limit`
 * changes but otherwise treats sessions as read-mostly — practice writes
 * land in IndexedDB but those updates won't show on this hook until next
 * mount/navigation, which is fine for stats panes.
 */
export function useRecentSessions(limit = 60): {
  sessions: SessionRecord[];
  loaded: boolean;
} {
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void getRecentSessions(limit).then((records) => {
      if (cancelled) return;
      setSessions(records);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [limit]);

  return { sessions, loaded };
}

/**
 * Look up the most recent unfinished session (endedAt: null). Resolves to
 * `undefined` if there isn't one. Like useRecentSessions, this is a
 * one-shot fetch on mount — Home only needs it to render a resume hint.
 */
export function useActiveSession(): {
  active: SessionRecord | undefined;
  loaded: boolean;
} {
  const [active, setActive] = useState<SessionRecord | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void getActiveSession().then((found) => {
      if (cancelled) return;
      setActive(found);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { active, loaded };
}
